import { createUrlWithParams } from '@/lib/urls';
import { beginCheckout, deleteLocalStorage, writeToLocalStorage } from '@/components/Checkout/utils/CheckoutProcess';
import { identifyUser } from '@/lib/analytics';
import * as Events from '@/analytics/events';
import { BlockKlaviyo } from '@/analytics/AnalyticsProvider';

/**
 * Handles the checkout submission process for both parent and teacher flows
 * @param {Object} params - Parameters for the checkout submission
 * @param {Object} params.data - Form data containing user input
 * @param {Object} params.router - Next.js router instance
 * @param {Object} params.analytics - Analytics instance for tracking events
 * @param {Function} params.beginCheckout - API function to initiate checkout
 * @param {Function} params.setLoading - Loading state setter function
 * @param {Function} params.setError - Error state setter function for form validation
 * @param {string} params.id - identifier for tracking purposes
 * @returns {Promise} Promise that resolves after handling checkout flow
 */

// Helper function to handle checkout flow
export const handlePreCheckout = async ({
    data,
    router,
    analytics,
    setLoading,
    setError,
    id
}) => {
    // Determine the current page type
    const isParentsPage = router.pathname === '/parents';
    const isTeachersPage = router.pathname === '/teachers';

    // Clear previous checkout state
    deleteLocalStorage('checkout-state');

    // Identify user and track initial email signup
    identifyUser({ email: data.email });
    await analytics.identify({ email: data.email });

    const initialEvent = {
        ...router.query,
        ...data,
        sourcePage: router.pathname,
        isParentsPage,
        isTeachersPage
    };

    await analytics.track(Events.EmailSignup, initialEvent);

    setLoading(true);

    // Prepare checkout payload
    const anonymousId = analytics.getAnonymousId();
    const logrocketSessionUrl = analytics.getLogrocketSessionURL();

    const payload = {
        email: data.email,
        'anonymous_id': anonymousId,
        'logrocket_session_url': logrocketSessionUrl,
        metadata: {
            ...data.metadata,
            'anonymous_id': anonymousId,
            'logrocket_session_url': logrocketSessionUrl,
            location: window.location.href,
            'input-id': id,
            'query-string': router.query
        }
    };

    return beginCheckout(payload)
        .then(async (response) => {
            const { token, email, error, errors } = response;

            // Handle errors if present
            if (error || errors) {
                await analytics.track(Events.EmailSignupError, {
                    ...response,
                    isParentsPage,
                    isTeachersPage,
                    beginCheckout: isParentsPage
                }, BlockKlaviyo);

                if (error) {
                    console.error(error);
                } else if (errors) {
                    for (const [key, value] of Object.entries(errors)) {
                        setError(key, { type: 'manual', message: value.toString() });
                    }
                }
                setLoading(false);
                return;
            }

            // Check if email is likely a teacher email
            const teacherEmail = /\b\.org\b|\b\.net\b|\b\.edu\b/.test(data.email);

            // Determine destination based on current page and email type
            let destPathname = '/onboarding';
            if (isTeachersPage) destPathname = '/signup';

            const successEvent = {
                ...router.query,
                ...data,
                email,
                teacherEmail,
                sourcePage: router.pathname,
                isParentsPage,
                isTeachersPage,
                token
            };
            // Track successful email signup
            // analytics.track(Events.EmailSignupSuccessful, successEvent);

            // Handle navigation based on user type
            if (isTeachersPage || teacherEmail) {
                const url = createUrlWithParams('/signup', { ...router.query, email, token });
                await analytics.track(Events.NewTeacherRegister, successEvent);
                return router.push(url, undefined, { shallow: false });
            } else {
                // main page - go to onboarding "I am a..."
                const destPath = createUrlWithParams(destPathname, { ...router.query, email, token });
                // return analytics.track(Events.UserTypeSelectionBegins, successEvent, {}, () => {
                // });
                await analytics.track(Events.UserTypeSelectionBegins, successEvent, BlockKlaviyo);
                await analytics.track(Events.NewUnknownRegister, successEvent, BlockKlaviyo);
                return router.push(destPath, undefined, { shallow: true });
            }
        })
        .catch((error) => {
            setLoading(false);
            console.error(error);
        });
};
